import React from 'react'
import { Fetch } from 'croods'
import { makeStyles } from '@material-ui/core/styles'
import map from 'lodash/map'
import get from 'lodash/get'
import filter from 'lodash/filter'
import groupBy from 'lodash/groupBy'
import sortBy from 'lodash/sortBy'

import Box from '@material-ui/core/Box'
import Container from '@material-ui/core/Container'
import Grid from '@material-ui/core/Grid'
import Hidden from '@material-ui/core/Hidden'

import { CLICK_USE_CASE_FOOTER_ITEM } from 'amplitude/events'
import useCurrentUser from 'auth/useCurrentUser'
import useSocialNetworks from 'utils/useSocialNetworks'
import UserType from 'utils/UserType'
import { colors } from 'theme'
import { useHasSubscriberPrivilege } from 'utils/useUserType'

import FooterLink from './NewFooterLink'
import FooterTop from './FooterTop'

import './Footer.css'

const getFooterSubTitles = subscribed => [
  'COMPANY',
  'LIBRARY',
  'RESOURCES',
  ...((!subscribed && ['READERS']) || []),
  'LEGAL',
  'CONNECT',
]

const audienceMenuItem = audience => ({
  id: audience.value,
  name: audience.title,
  slug: `subscribe/?audience=${audience.value}`,
  customFieldValues: {
    resourceType: 'footer-use-cases',
  },
  eventName: CLICK_USE_CASE_FOOTER_ITEM,
  eventProps: { audience: audience.value },
  onClick: () => {
    window.scrollTo(0, 0)
  },
})

const audienceItemList = [
  'STUDENT',
  'TEACHER',
  'BOOK_CLUB_MEMBER',
  'PARENT',
].map(audienceKey => audienceMenuItem(UserType[audienceKey]))

const addPages = path => {
  return [
    {
      id: 'about-us',
      name: 'About Us',
      slug: 'about-us',
      external: true,
      customFieldValues: {
        resourceType: 'footer-about',
      },
    },
    {
      id: 'wall-of-love',
      name: 'Wall of Love',
      slug: 'testimonials',
      external: true,
      customFieldValues: {
        resourceType: 'footer-about',
      },
      eventName: 'Click Into Testimonials Page',
      eventProps: {
        'Button Type': 'Footer Link',
      },
    },
    {
      id: 'view-all-titles',
      name: 'Study Guides',
      slug: 'all-study-guides',
      external: true,
      customFieldValues: {
        resourceType: 'footer-library',
      },
    },
    {
      id: 'all-teaching-guides',
      name: 'Teaching Guides',
      slug: 'all-teaching-guides',
      external: true,
      customFieldValues: {
        resourceType: 'footer-library',
      },
    },
    {
      id: 'plot-summaries',
      name: 'Plot Summaries',
      slug: 'plot-summaries',
      external: true,
      customFieldValues: {
        resourceType: 'footer-library',
      },
    },
    {
      id: 'collections',
      name: 'Study Guide Collections',
      slug: 'collections',
      external: true,
      customFieldValues: {
        resourceType: 'footer-library',
      },
      eventName: 'Click Into Collections Directory Page',
      eventProps: {
        'Button Type': 'Footer',
      },
    },
    {
      id: 'suggest-book-title',
      name: 'Suggest a Title',
      slug: 'suggest-book-title',
      customFieldValues: {
        resourceType: 'footer-library',
      },
    },
    {
      id: 'literary-devices',
      name: 'Literary Devices',
      slug: 'literary-devices',
      customFieldValues: {
        resourceType: 'footer-resources',
      },
    },
    {
      id: 'resources',
      name: 'Other Resources',
      slug: 'resources',
      customFieldValues: {
        resourceType: 'footer-resources',
      },
    },
    {
      id: 'discussion-questions',
      name: 'Discussion Questions Tool',
      slug: 'tools/discussion-questions',
      external: true,
      customFieldValues: {
        resourceType: 'footer-resources',
      },
      eventName: 'Footer Item Clicked',
      eventProps: {
        label: 'Discussion Questions Tool',
        on_click_behavior: 'Page Redirection',
        origin_url: path,
        destination_url: '/tools/discussion-questions/',
        platform: 'Web',
      },
    },
    ...audienceItemList,
  ]
}

const Footer = ({ oldVersion }) => {
  const classes = useStyles({ oldVersion })
  const socialNetworks = useSocialNetworks(!!oldVersion)
  const [{ currentUser }] = useCurrentUser()
  const hasSubscriberPrivilege = useHasSubscriberPrivilege()
  const footerBlockTitle = getFooterSubTitles(currentUser?.subscribed)

  return (
    <Fetch
      name="footer"
      customPath="/pages/footer"
      cache
      renderLoading={() => null}
      render={staticPages => {
        const sortedStaticPages = sortBy(
          staticPages,
          'customFieldValues.sorting',
        )

        const footerPages = addPages(window.location.pathname)

        const customStaticPages = [
          ...footerPages,
          ...sortedStaticPages,
          ...socialNetworks,
        ]

        const pages = map(
          groupBy(
            filter(
              customStaticPages,
              page =>
                get(page, 'customFieldValues.resourceType') ===
                  'footer-about' ||
                get(page, 'customFieldValues.resourceType') ===
                  'footer-resources' ||
                get(page, 'customFieldValues.resourceType') ===
                  'footer-legal' ||
                get(page, 'customFieldValues.resourceType') ===
                  'footer-connect' ||
                get(page, 'customFieldValues.resourceType') ===
                  'footer-library' ||
                (!currentUser?.subscribed &&
                  get(page, 'customFieldValues.resourceType') ===
                    'footer-use-cases'),
            ),
            'customFieldValues.resourceType',
          ),
        )

        return (
          <Box
            component="footer"
            alignSelf="stretch"
            className={classes.footerBox}
          >
            {hasSubscriberPrivilege ? null : <FooterTop />}
            <Box
              bgcolor={oldVersion ? 'primary.main' : undefined}
              alignSelf="stretch"
              className={classes.innerBox}
            >
              <Container className={classes.container}>
                <Hidden xsDown>
                  <img
                    className={classes.img}
                    src={oldVersion ? '/logo.png' : '/colored-logo.svg'}
                    alt="logo"
                    loading={oldVersion ? undefined : 'lazy'}
                  />
                </Hidden>
                <Grid item container spacing={3} justify="flex-end">
                  {map(pages, (page, key) => (
                    <FooterLink
                      key={footerBlockTitle[key]}
                      title={footerBlockTitle[key]}
                      page={page}
                      oldVersion={oldVersion}
                    />
                  ))}
                </Grid>
              </Container>
            </Box>
          </Box>
        )
      }}
    />
  )
}

const useStyles = makeStyles(theme => ({
  footerBox: ({ oldVersion }) => ({
    zIndex: theme.zIndex.drawer + 1,
    ...(oldVersion ? {} : { borderTop: `1px solid ${colors.chipBackground}` }),
  }),
  innerBox: ({ oldVersion }) => ({
    padding: theme.spacing(2, 0),
    ...(oldVersion ? {} : { background: colors.white }),
  }),
  container: ({ oldVersion }) => ({
    padding: theme.spacing(0, 0, 5, 5),
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2),
    },
    maxWidth: 'min(960px, calc(100% - 24px))',
    ...(oldVersion ? {} : { textAlign: 'center' }),
  }),
  img: ({ oldVersion }) => ({
    width: theme.spacing(oldVersion ? 20 : 24),
    padding: theme.spacing(2, 0, 5),
  }),
}))

export default Footer
